// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { AvailabilityMessageType, CartLineModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import CartLinesListHeader, { CartLinesListHeaderStyles } from "@insite/content-library/Components/CartLinesListHeader";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import CartLineCardCondensed, { CartLineCardCondensedStyles } from "@rugby/Overrides/Components/CartLineCardCondensed";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@rugby/Overrides/Components/CartLineCardExpanded";
import RugbyCartLinesHeader from "@rugby/Overrides/Widgets/Cart/RugbyCartLinesHeader";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    editable: boolean;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    extendedStyles?: CartLinesListStyles;
}

export interface CartLinesListStyles {
    wrapper?: InjectableCss;
    header?: CartLinesListHeaderStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    pagination?: PaginationPresentationProps;
}

export const orderLinesListStyles: CartLinesListStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
};

const CartLinesList: FC<OwnProps> = ({
    cart,
    promotions,
    isCondensed,
    hideCondensedSelector,
    onChangeIsCondensed,
    editable,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    if (!cart.cartLines) {
        return null;
    }

    const cartLines =
        cart.status === "Cart" ? cart.cartLines.filter(cartline => !cartline.quoteRequired) : cart.cartLines;

    const cartLinesAvailabilityGroup: CartLineModel[] = [];
    const cartLinesOtherGroup = cartLines.filter(item => {
        if (item.availability?.messageType !== AvailabilityMessageType.OutOfStock) {
            cartLinesAvailabilityGroup.push(item);
            return false;
        }
        return true;
    });

    const cartLinesGroup = [cartLinesAvailabilityGroup, cartLinesOtherGroup];
    const cartLinesHeaderGroup = ["Immediately Available", "Contact us for Lead times"];

    const CartLineComponent = isCondensed ? CartLineCardCondensed : CartLineCardExpanded;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`cardLinesList_${cart.id}`}>
            <CartLinesListHeader
                productsCount={cartLines.length}
                isCondensed={isCondensed}
                hideCondensedSelector={hideCondensedSelector}
                onChangeIsCondensed={onChangeIsCondensed}
                extendedStyles={styles.header}
            />
            {cartLinesGroup.map((cartLines, cartLinesGroupIndex) => {
                if (!cartLines.length) {
                    return null;
                }
                const marginBottom = cartLinesGroup.length - 1 === cartLinesGroupIndex ? 0 : 30;
                return (
                    <StyledWrapper key={uniqueId()}>
                        <RugbyCartLinesHeader leftHeaderTitle={cartLinesHeaderGroup[cartLinesGroupIndex]} />
                        <StyledWrapper style={{ marginBottom }}>
                            {cartLines!.map(cartLine => {
                                const cartLinePromotions = promotions?.filter(
                                    promotion => promotion.orderLineId === cartLine.id,
                                );
                                return (
                                    <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                                        <CartLineComponent
                                            cart={cart}
                                            promotions={cartLinePromotions}
                                            editable={editable}
                                            showSavingsAmount={showSavingsAmount}
                                            showSavingsPercent={showSavingsPercent}
                                            showInvalidPriceMessage={true}
                                            extendedStyles={
                                                (isCondensed
                                                    ? styles.cardCondensed
                                                    : styles.cardExpanded) as CartLineCardCondensedStyles &
                                                    CartLineCardExpandedStyles
                                            }
                                        />
                                    </CartLineContext.Provider>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

export default CartLinesList;
